import React, { useState, useMemo } from 'react';
import type { 
  DrumInstrument, 
  DrumGridData, 
  TimeSignature, 
  NoteDivision 
} from '../types';
import { useThemeContext } from '../context/ThemeProvider';
import { useLanguage } from '../context/LanguageProvider';
import DrumRNNButton from './DrumRNNButton';
import SeedStatusIndicator from './SeedStatusIndicator';
import DrumRNNSeedConverter from './DrumRNNSeedConverter';
import { devLog } from '../utils/devLog';

interface DrumRNNPanelProps {
  instruments: DrumInstrument[];
  grid: DrumGridData;
  setGrid: (grid: DrumGridData) => void;
  stepsPerBar: number;
  setStepsPerBar: (steps: number) => void;
  timeSignature: TimeSignature;
  noteDivision?: NoteDivision;
  selectedPattern?: string;
  onGridModified?: () => void;
  isGridModified?: boolean;
  temperature: number;
  setTemperature: (temp: number) => void;
}

interface SeedConfig {
  presetPattern: string | null;
  useSeedFromPattern: boolean;
  seedOptions: {
    velocity?: number;
    repeatCount?: number;
    addVariation?: boolean;
    targetSteps?: number;
  };
}

/**
 * DrumRNN整合面板
 * 包含AI生成、seed管理、temperature調整等功能
 */
const DrumRNNPanel: React.FC<DrumRNNPanelProps> = ({
  instruments,
  grid,
  setGrid,
  stepsPerBar,
  setStepsPerBar,
  timeSignature,
  noteDivision,
  selectedPattern,
  onGridModified,
  isGridModified,
  temperature,
  setTemperature
}) => {
  const { getClass } = useThemeContext();
  const { t } = useLanguage();
  
  // 開發模式狀態
  const [showAdvanced, setShowAdvanced] = useState(process.env.NODE_ENV === 'development');
  
  // 強制隨機生成模式
  const [forceRandomGeneration, setForceRandomGeneration] = useState(false);

  // 檢查grid是否有內容
  const hasGridContent = useMemo(() => {
    return Object.values(grid).some(row => 
      row && row.some(cell => cell !== null && cell !== undefined)
    );
  }, [grid]);

  // 智能判斷seed設定
  const getSeedConfig = (): SeedConfig => {
    // 如果強制隨機生成，直接返回隨機設定
    if (forceRandomGeneration) {
      return {
        presetPattern: null,
        useSeedFromPattern: false,
        seedOptions: {}
      };
    }
    
    if (selectedPattern && !isGridModified) {
      // 使用preset pattern作為seed
      return {
        presetPattern: selectedPattern,
        useSeedFromPattern: true,
        seedOptions: {
          velocity: 80,
          repeatCount: 1,
          addVariation: false  // 對於preset pattern，不需要variation，保持一致性
        }
      };
    } else if (hasGridContent) {
      // 使用當前grid作為seed
      return {
        presetPattern: null,
        useSeedFromPattern: true,
        seedOptions: {
          velocity: 80,
          addVariation: false
        }
      };
    } else {
      // 純隨機生成
      return {
        presetPattern: null,
        useSeedFromPattern: false,
        seedOptions: {}
      };
    }
  };

  const seedConfig = getSeedConfig();

  // Temperature滑桿變更處理 - 將UI的0.1-1.0映射到實際的1.1-2.0
  const handleTemperatureChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const displayValue = parseFloat(event.target.value);
    // 映射 0.1-1.0 到 1.1-2.0
    const actualValue = 1.1 + (displayValue - 0.1) * (2.0 - 1.1) / (1.0 - 0.1);
    setTemperature(Math.round(actualValue * 10) / 10); // 修正浮點數精度
  };
  
  // 將實際溫度(1.1-2.0)轉換為顯示值(0.1-1.0)
  const getDisplayTemperature = (): number => {
    const displayValue = 0.1 + (temperature - 1.1) * (1.0 - 0.1) / (2.0 - 1.1);
    return Math.round(displayValue * 10) / 10; // 修正浮點數精度
  };

  // 主題類別獲取函數
  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
      };
      return fallback[type] || '';
    }
  };

  return (
    <div className={`overflow-x-auto space-y-3 p-4 rounded ${getThemeClass('grid')} ${getThemeClass('border')}`}>
      {/* 精簡為三行的控制區域 */}
      <div className="space-y-2">
        {/* 第一行：AI生成 + 創意度: 1.0 + 保守/平衡/創意/實驗 */}
        <div className="flex items-center space-x-4">
          <h2 className="text-lg md:text-xl font-bold">🤖 <span className="hidden md:inline">{t('drumRNN.title') || 'AI Generation'}</span><span className="md:hidden">{t('drumRNN.titleMobile') || 'AI Gen'}</span></h2>
          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${getThemeClass('text')}`}>
              {t('drumRNN.temperature') || 'Creativity'}: {getDisplayTemperature().toFixed(1)}
            </label>
            <span className={`text-xs ${getThemeClass('text')} opacity-70`}>
              {getDisplayTemperature() < 0.3 ? t('drumRNN.conservative') || 'Conservative' :
               getDisplayTemperature() < 0.6 ? t('drumRNN.balanced') || 'Balanced' :
               getDisplayTemperature() < 0.8 ? t('drumRNN.creative') || 'Creative' :
               t('drumRNN.experimental') || 'Experimental'}
            </span>
          </div>
          
          {/* Seed狀態指示器 */}
          <div className="relative group">
            <SeedStatusIndicator 
              selectedPattern={selectedPattern}
              isGridModified={isGridModified}
              hasGridContent={hasGridContent}
              forceRandom={forceRandomGeneration}
            />
          </div>
          
          {process.env.NODE_ENV === 'development' && (
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`px-2 py-1 rounded text-xs transition-all duration-200 ${
                showAdvanced ? getThemeClass('buttonWarning') : getThemeClass('buttonSecondary')
              }`}
              title={showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
            >
              <span>{showAdvanced ? '🔧' : '👁️'}</span>
              <span className="hidden lg:inline ml-1">{t('debugMode')}</span>
            </button>
          )}
        </div>

        {/* 第二行：滑桿 */}
        <div className="relative max-w-md">
          <input
            type="range"
            min="0.1"
            max="1.0"
            step="0.1"
            value={getDisplayTemperature()}
            onChange={handleTemperatureChange}
            className={`w-full h-2 rounded-lg appearance-none cursor-pointer slider
              ${getThemeClass('input')} focus:outline-none focus:ring-2 focus:ring-blue-500
            `}
            style={{
              background: `linear-gradient(to right, 
                #3b82f6 0%, 
                #10b981 ${((getDisplayTemperature() - 0.1) / 0.9) * 40}%, 
                #f59e0b ${((getDisplayTemperature() - 0.1) / 0.9) * 70}%,
                #ef4444 ${((getDisplayTemperature() - 0.1) / 0.9) * 100}%)`
            }}
          />
          <style>{`
            .slider::-webkit-slider-thumb {
              appearance: none;
              height: 16px;
              width: 16px;
              border-radius: 50%;
              background: #ffffff;
              border: 2px solid #3b82f6;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .slider::-moz-range-thumb {
              height: 16px;
              width: 16px;
              border-radius: 50%;
              background: #ffffff;
              border: 2px solid #3b82f6;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
          `}</style>
          {/* 刻度標記 */}
          <div className="flex justify-between text-xs mt-1 opacity-60">
            <span>0.1</span>
            <span>0.3</span>
            <span>0.5</span>
            <span>0.8</span>
            <span>1.0</span>
          </div>
        </div>

        {/* 第三行：生成按鈕 + Force Random checkbox */}
        <div className="flex items-center space-x-4">
          <DrumRNNButton 
            instruments={instruments}
            grid={grid}
            setGrid={setGrid}
            setStepsPerBar={setStepsPerBar}
            timeSignature={timeSignature}
            noteDivision={noteDivision || '16n'}
            stepsPerBar={stepsPerBar}
            temperature={temperature}
            presetPattern={seedConfig.presetPattern}
            useSeedFromPattern={seedConfig.useSeedFromPattern}
            seedOptions={seedConfig.seedOptions}
          />
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={forceRandomGeneration}
              onChange={(e) => setForceRandomGeneration(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className={`text-sm ${getThemeClass('text')}`}>
              {t('drumRNN.forceRandom') || '強制隨機'}
            </span>
          </label>
        </div>

        {/* 開發模式詳細信息 */}
        {showAdvanced && (
          <div className={`p-3 rounded border-2 border-orange-400 ${getThemeClass('background')} space-y-3`}>
            <h4 className="font-bold text-orange-600 mb-2">🔧 {t('drumRNN.dev.developerInfo') || 'Developer Information'}</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-mono">
              <div className="space-y-1">
                <strong className="text-orange-700">{t('drumRNN.dev.seedConfig') || 'Seed Configuration'}:</strong><br/>
                <span>{t('drumRNN.dev.type') || '類型'}: {forceRandomGeneration ? '🎲 強制隨機' : (seedConfig.useSeedFromPattern ? 
                  (seedConfig.presetPattern ? t('drumRNN.dev.presetPattern') || '預設節拍' : t('drumRNN.dev.grid') || '網格') : t('drumRNN.dev.random') || '隨機')}</span><br/>
                <span>{t('drumRNN.dev.pattern') || '節拍'}: {forceRandomGeneration ? '(忽略)' : (seedConfig.presetPattern ? t(`drumPatterns.${seedConfig.presetPattern}`) || seedConfig.presetPattern : t('drumRNN.dev.none') || '無')}</span><br/>
                <span>{t('drumRNN.dev.modified') || '已修改'}: {forceRandomGeneration ? '(忽略)' : (isGridModified ? t('drumRNN.dev.yes') || '是' : t('drumRNN.dev.no') || '否')}</span><br/>
                <span>{t('drumRNN.dev.hasContent') || '有內容'}: {forceRandomGeneration ? '(忽略)' : (hasGridContent ? t('drumRNN.dev.yes') || '是' : t('drumRNN.dev.no') || '否')}</span><br/>
                <span>{t('drumRNN.dev.targetSteps') || '目標步數'}: {stepsPerBar}</span><br/>
                <span>{t('drumRNN.forceRandom')}: {forceRandomGeneration ? `✅ ${t('drumRNN.enabled')}` : `❌ ${t('drumRNN.disabled')}`}</span>
              </div>
              
              <div className="space-y-1">
                <strong className={getThemeClass('text')}>{t('drumRNN.dev.genSettings') || 'Generation Settings'}:</strong><br/>
                <span>{t('drumRNN.dev.temperature') || '溫度'}: {temperature}</span><br/>
                <span>{t('drumRNN.dev.timeSignature') || '拍號'}: {timeSignature}</span><br/>
                <span>{t('drumRNN.dev.noteDivision') || '音符分割'}: {noteDivision}</span><br/>
                <span>{t('drumRNN.dev.stepsPerBar') || '小節步數'}: {stepsPerBar}</span><br/>
                <span>{t('drumRNN.dev.aiCreativity') || 'AI創意度'}: {temperature < 0.5 ? t('drumRNN.conservative') || '保守' :
                     temperature < 1.0 ? t('drumRNN.balanced') || '平衡' :
                     temperature < 1.5 ? t('drumRNN.creative') || '創意' : 
                     t('drumRNN.experimental') || '實驗'}</span>
              </div>
              
              <div className="space-y-1">
                <strong className={getThemeClass('text')}>{t('drumRNN.dev.qualityCheck') || 'Quality Check'}:</strong><br/>
                <span>{t('drumRNN.dev.tempEffect') || '低溫度影響'}: {temperature <= 0.3 ? `✅ ${t('drumRNN.dev.expectedMinimal') || '預期極少變化'}` : `⚠️ ${t('drumRNN.dev.mayVary') || '可能有變化'}`}</span><br/>
                <span>{t('drumRNN.dev.tempAdvice') || '溫度建議'}: {temperature <= 0.3 ? t('drumRNN.dev.shouldKeepSimilar') || '應保持seed相似度' : temperature >= 1.5 ? t('drumRNN.dev.mayVarySignificantly') || '可能大幅變化' : t('drumRNN.dev.moderateChange') || '適度變化'}</span><br/>
                <span>{t('drumRNN.dev.patternRepeats') || 'Pattern Repeats'}: {seedConfig.presetPattern && stepsPerBar > 16 ? `${Math.ceil(stepsPerBar / 16)}x` : '1x'}</span><br/>
                <span>{t('drumRNN.dev.seedLength') || 'Seed Length'}: {seedConfig.useSeedFromPattern ? 
                  (seedConfig.presetPattern ? `16 steps (${t('drumRNN.dev.repeated') || '重複'})` : `${stepsPerBar} steps`) : 
                  `0 steps (${t('drumRNN.dev.randomGen') || '隨機'})`}</span><br/>
                <span>{t('drumRNN.dev.expectedOutput') || 'Expected Output'}: {stepsPerBar} steps</span>
              </div>
            </div>

            {/* Seed選項詳情 */}
            {seedConfig.useSeedFromPattern && (
              <div className="space-y-1">
                <strong className={getThemeClass('text')}>Seed選項:</strong><br/>
                <pre className={`text-xs p-2 rounded overflow-x-auto ${getThemeClass('grid')}`}>
                  {JSON.stringify(seedConfig.seedOptions, null, 2)}
                </pre>
              </div>
            )}
            
            {/* 實時Seed統計 */}
            {seedConfig.useSeedFromPattern && seedConfig.presetPattern && (
              <div className="space-y-1">
                <strong className={getThemeClass('text')}>Seed統計 (實時):</strong><br/>
                <div className={`text-xs p-2 rounded ${getThemeClass('grid')}`}>
                  {(() => {
                    try {
                      const seed = DrumRNNSeedConverter.convertPatternToSeed(seedConfig.presetPattern!, {
                        ...seedConfig.seedOptions,
                        targetSteps: stepsPerBar
                      });
                      const notesByPitch: Record<number, number> = {};
                      seed.notes.forEach(note => {
                        if (!notesByPitch[note.pitch]) notesByPitch[note.pitch] = 0;
                        notesByPitch[note.pitch]++;
                      });
                      const instrumentNames: Record<number, string> = {36: 'BD', 38: 'SD', 42: 'CH', 46: 'OH', 39: 'CP', 49: 'CR'};
                      
                      return (
                        <div>
                          <div>總音符數: {seed.notes.length}</div>
                          <div>預期音符數: {stepsPerBar === 32 ? '約34個 (16步x2)' : `約${Math.floor(seed.notes.length/2)}個`}</div>
                          <div className="mt-1">樂器分布:</div>
                          {Object.entries(notesByPitch).map(([pitch, count]) => (
                            <div key={pitch} className="ml-2">
                              {instrumentNames[parseInt(pitch)] || `MIDI${pitch}`}: {count}個
                            </div>
                          ))}
                        </div>
                      );
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                      return <div className="text-red-600">生成錯誤: {errorMessage}</div>;
                    }
                  })()}
                </div>
              </div>
            )}
            
            {/* 開發模式測試按鈕 */}
            <div className="flex space-x-2 mt-3">
              <button
                onClick={() => {
                  const seedConfig = getSeedConfig();
                  devLog('🔍 Seed Configuration Debug:', seedConfig);
                  if (seedConfig.presetPattern) {
                    try {
                      const seed = DrumRNNSeedConverter.convertPatternToSeed(seedConfig.presetPattern, {
                        ...seedConfig.seedOptions,
                        targetSteps: stepsPerBar
                      });
                      devLog('🌱 Generated Seed:', seed);
                      devLog(`📊 Notes count: ${seed.notes.length}, Expected for ${stepsPerBar} steps`);
                      // 按樂器分組顯示
                      const notesByPitch: Record<number, number[]> = {};
                      seed.notes.forEach(note => {
                        const pitch = note.pitch;
                        if (!notesByPitch[pitch]) notesByPitch[pitch] = [];
                        notesByPitch[pitch].push(note.quantizedStartStep);
                      });
                      devLog('📝 Notes by instrument (MIDI pitch):', notesByPitch);
                    } catch (error) {
                      console.error('❌ Seed generation failed:', error);
                    }
                  } else {
                    devLog('⚠️ No preset pattern selected');
                  }
                }}
                className={`px-3 py-1 rounded text-xs ${getThemeClass('button')}`}
              >
                🔍 {t('drumRNN.dev.testSeedGeneration') || 'Test Seed Generation'}
              </button>
              
              <button
                onClick={() => {
                  devLog('📊 Current State:');
                  devLog('- Temperature:', temperature);
                  devLog('- Steps per Bar:', stepsPerBar);
                  devLog('- Selected Pattern:', selectedPattern);
                  devLog('- Grid Modified:', isGridModified);
                  devLog('- Has Grid Content:', hasGridContent);
                }}
                className={`px-3 py-1 rounded text-xs ${getThemeClass('button')}`}
              >
                📊 {t('drumRNN.dev.logCurrentState') || 'Log Current State'}
              </button>
            </div>

            {/* 測試功能 */}
            <div className={`flex space-x-2 pt-2 border-t ${getThemeClass('border')}`}>
              <button 
                onClick={() => devLog('🎯 Current Seed Config:', seedConfig)}
                className={`px-2 py-1 rounded text-xs ${getThemeClass('button')}`}
              >
                {t('drumRNN.dev.logSeedConfig') || 'Log Seed Config'}
              </button>
              <button 
                onClick={() => {
                  try {
                    if (seedConfig.presetPattern) {
                      const seed = DrumRNNSeedConverter.convertPatternToSeed(seedConfig.presetPattern, {
                        ...seedConfig.seedOptions,
                        targetSteps: stepsPerBar
                      });
                      devLog('🌱 Generated Seed from Pattern:', seed);
                    } else if (hasGridContent) {
                      const seed = DrumRNNSeedConverter.convertGridToSeed(grid, instruments, timeSignature, noteDivision);
                      devLog('🔄 Generated Seed from Grid:', seed);
                    } else {
                      devLog('🎲 No seed - random generation');
                    }
                  } catch (error) {
                    console.error('❌ Preview Seed failed:', error);
                  }
                }}
                className={`px-2 py-1 rounded text-xs ${getThemeClass('button')}`}
              >
                {t('drumRNN.dev.previewSeed') || 'Preview Seed'}
              </button>
              <button 
                onClick={() => {
                  devLog('📊 DrumRNN Panel State:', {
                    selectedPattern,
                    isGridModified,
                    hasGridContent,
                    temperature,
                    seedConfig
                  });
                }}
                className={`px-2 py-1 rounded text-xs ${getThemeClass('button')}`}
              >
                {t('drumRNN.dev.logPanelState') || 'Log Panel State'}
              </button>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default DrumRNNPanel; 